import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import React, { useState, useEffect, useRef, Suspense, useCallback } from "react";
import { Toaster } from "@/components/ui/toaster";
import { useAuthStore } from "@/stores/authStore";
import { getAccessToken } from "@/services/api";
import { isCustomer } from "@/constants/userTypes";

// Layouts - eager (structural, always needed)
import MainLayout from "@/components/layout/MainLayout";
import SubscriptionGate from "@/components/billing/SubscriptionGate";
import AuthLayout from "@/components/layout/AuthLayout";

// Error Pages - eager (small, needed immediately)
import ForbiddenPage from "@/pages/error/ForbiddenPage";
import NoPermissionGroupPage from "@/pages/error/NoPermissionGroupPage";

// Auth Pages - eager (needed for auth flow before main app loads)
import LoginPage from "@/pages/auth/LoginPage";
import RegisterPage from "@/pages/auth/RegisterPage";
import CompanyRegisterPage from "@/pages/auth/CompanyRegisterPage";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";
import ForceChangePasswordPage from "@/pages/auth/ForceChangePasswordPage";
import ConsentPage from "@/pages/auth/ConsentPage";
import NoCompanyPage from "@/pages/auth/NoCompanyPage";

// DocumentSubmissionPage - eager (used inline in ProtectedRoute)
import DocumentSubmissionPage from "@/pages/document-submission/DocumentSubmissionPage";
// DailyReportRequiredPage - eager (익일 차단, ProtectedRoute 인라인 렌더)
import DailyReportRequiredPage from "@/pages/worklog/DailyReportRequiredPage";

// ─── Lazy-loaded Pages ──────────────────────────────────────────────────────

// Landing (공개 마케팅 페이지)
const LandingPage = React.lazy(() => import("@/pages/landing/LandingPage"));
const DesktopHandoffPage = React.lazy(() => import("@/pages/auth/DesktopHandoffPage"));

// Dashboard
const DashboardPage = React.lazy(() => import("@/pages/dashboard/DashboardPage"));

// Maintenance
const TicketListPage = React.lazy(() => import("@/pages/maintenance/TicketListPage"));
const TicketDetailPage = React.lazy(() => import("@/pages/maintenance/TicketDetailPage"));
const TicketCreatePage = React.lazy(() => import("@/pages/maintenance/TicketCreatePage"));
const MaintenanceDashboardPage = React.lazy(() => import("@/pages/maintenance/MaintenanceDashboardPage"));

// SLA
const SLADashboardPage = React.lazy(() => import("@/pages/sla/SLADashboardPage"));

// Projects
const ProjectListPage = React.lazy(() => import("@/pages/projects/ProjectListPage"));
const ProjectKanbanPage = React.lazy(() => import("@/pages/projects/ProjectKanbanPage"));
const ProjectDetailPage = React.lazy(() => import("@/pages/projects/ProjectDetailPage"));
const ProjectBoardPage = React.lazy(() => import("@/pages/projects/ProjectBoardPage"));
const ProjectBoardPostsPage = React.lazy(() => import("@/pages/projects/ProjectBoardPostsPage"));
const ProjectSettingsPage = React.lazy(() => import("@/pages/projects/ProjectSettingsPage"));
const ProjectGanttPage = React.lazy(() => import("@/pages/projects/ProjectGanttPage"));
const ProjectSLADashboardPage = React.lazy(() => import("@/pages/projects/ProjectSLADashboardPage"));
const MyTasksPage = React.lazy(() => import("@/pages/projects/MyTasksPage"));
const EmployeeWorkloadGanttPage = React.lazy(() => import("@/pages/tasks/EmployeeWorkloadGanttPage"));

// Chat
const ChatPage = React.lazy(() => import("@/pages/chat/ChatPage"));
const ChatPopupPage = React.lazy(() => import("@/pages/chat/ChatPopupPage"));

// Calendar
const CalendarPage = React.lazy(() => import("@/pages/calendar/CalendarPage"));

// Files
const FilesPage = React.lazy(() => import("@/pages/files/FilesPage"));
const ShareReceivePage = React.lazy(() => import("@/pages/files/ShareReceivePage"));

// Sticky Notes
const StickyNotesPage = React.lazy(() => import("@/pages/StickyNotesPage"));

// Board
const BoardListPage = React.lazy(() => import("@/pages/board/BoardListPage"));
const BoardDetailPage = React.lazy(() => import("@/pages/board/BoardDetailPage"));
const NoticeBoardPage = React.lazy(() => import("@/pages/board/NoticeBoardPage"));
const FreeBoardPage = React.lazy(() => import("@/pages/board/FreeBoardPage"));
const ArchiveBoardPage = React.lazy(() => import("@/pages/board/ArchiveBoardPage"));
const WeeklyReportBoardPage = React.lazy(() => import("@/pages/board/WeeklyReportBoardPage"));

// Approval (EA)
// 전자결재 v2 (EA)
const EaInboxPage = React.lazy(() => import("@/pages/ea/EaInboxPage"));
const EaComposePage = React.lazy(() => import("@/pages/ea/EaComposePage"));
const EaDocumentPage = React.lazy(() => import("@/pages/ea/EaDocumentPage"));
const EaPrintPage = React.lazy(() => import("@/pages/ea/EaPrintPage"));
const EaProcessingPage = React.lazy(() => import("@/pages/ea/EaProcessingPage"));
const EaSettingsPage = React.lazy(() => import("@/pages/ea/EaSettingsPage"));
const EaLegacyRedirect = React.lazy(() => import("@/pages/ea/EaLegacyRedirect"));

// Billing / Subscription
const PlansPage = React.lazy(() => import("@/pages/billing/PlansPage"));
const BillingPage = React.lazy(() => import("@/pages/billing/BillingPage"));
const BillingLockedPage = React.lazy(() => import("@/pages/billing/BillingLockedPage"));
const PlansManagePage = React.lazy(() => import("@/pages/admin/billing/PlansManagePage"));
const BillingInquiriesPage = React.lazy(() => import("@/pages/admin/billing/InquiriesPage"));
const BillingSettingsPage = React.lazy(() => import("@/pages/admin/billing/BillingSettingsPage"));
const RevenueDashboardPage = React.lazy(() => import("@/pages/admin/billing/RevenueDashboardPage"));

// Attendance
const AttendancePage = React.lazy(() => import("@/pages/attendance/AttendancePage"));
const LeavePage = React.lazy(() => import("@/pages/attendance/LeavePage"));
const HalfDayLeavePage = React.lazy(() => import("@/pages/attendance/HalfDayLeavePage"));
const MyWeeklySchedulePage = React.lazy(() => import("@/pages/attendance/MyWeeklySchedulePage"));
const TeamWeeklySchedulePage = React.lazy(() => import("@/pages/attendance/TeamWeeklySchedulePage"));

// Employee Status
const EmployeeStatusPage = React.lazy(() => import("@/pages/employee-status/EmployeeStatusPage"));

// Admin Attendance
const HalfDayLeaveManagementPage = React.lazy(() => import("@/pages/admin/HalfDayLeaveManagementPage"));
const ManualHalfDayGrantPage = React.lazy(() => import("@/pages/admin/ManualHalfDayGrantPage"));
const HalfDayLeaveRulesPage = React.lazy(() => import("@/pages/admin/HalfDayLeaveRulesPage"));

// Worklog
const WorklogPage = React.lazy(() => import("@/pages/worklog/WorklogPage"));
const DailyReportSettingsPage = React.lazy(() => import("@/pages/worklog/DailyReportSettingsPage"));
const WeeklyReportProPage = React.lazy(() => import("@/pages/worklog/WeeklyReportProPage"));

// Expense

// Purchase Request

// Card Approval
const MyCardUsagePage = React.lazy(() => import("@/pages/card/MyCardUsagePage"));

// Corporate Card Admin
const CorporateCardManagementPage = React.lazy(() => import("@/pages/admin/CorporateCardManagementPage"));
const CardUsageUploadPage = React.lazy(() => import("@/pages/admin/CardUsageUploadPage"));
const CardUsageDetailPage = React.lazy(() => import("@/pages/admin/CardUsageDetailPage"));

// Admin
const AdminDashboardV2 = React.lazy(() => import("@/pages/admin/AdminDashboardV2"));
const CustomerManagementPage = React.lazy(() => import("@/pages/admin/CustomerManagementPage"));
const SystemSettings = React.lazy(() => import("@/pages/admin/SystemSettings"));
const CompanyManagementPage = React.lazy(() => import("@/pages/admin/CompanyManagementPage"));
const SuperAdminCompaniesPage = React.lazy(() => import("@/pages/admin/SuperAdminCompaniesPage"));
const SuperAdminCompanyConfigPage = React.lazy(() => import("@/pages/admin/SuperAdminCompanyConfigPage"));
const DefaultMenuSettingsPage = React.lazy(() => import("@/pages/super-admin/DefaultMenuSettingsPage"));
const AISettingsPage = React.lazy(() => import("@/pages/super-admin/AISettingsPage"));
const AIUsagePage = React.lazy(() => import("@/pages/super-admin/AIUsagePage"));
const SuperAdminMembersPage = React.lazy(() => import("@/pages/super-admin/MembersPage"));
const ResourceUsagePage = React.lazy(() => import("@/pages/admin/ResourceUsagePage"));
const StorageUsagePage = React.lazy(() => import("@/pages/admin/StorageUsagePage"));
const AuditLogPage = React.lazy(() => import("@/pages/admin/AuditLogPage"));
const ConsentRecordsPage = React.lazy(() => import("@/pages/admin/ConsentRecordsPage"));
const ConsentDocumentManagePage = React.lazy(() => import("@/pages/admin/ConsentDocumentManagePage"));
const NotificationManagementPage = React.lazy(() => import("@/pages/admin/NotificationManagementPage"));
const ScheduledNotificationsPage = React.lazy(() => import("@/pages/admin/ScheduledNotificationsPage"));
const FileTrashPage = React.lazy(() => import("@/pages/admin/FileTrashPage"));
const FileLogsPage = React.lazy(() => import("@/pages/admin/FileLogsPage"));
const AdminPermissionGroupsPage = React.lazy(() => import("@/pages/admin/AdminPermissionGroupsPage"));
const AdminMenusPage = React.lazy(() => import("@/pages/admin/AdminMenusPage"));
const PermissionGroupMenuBuilderPage = React.lazy(() => import("@/pages/admin/PermissionGroupMenuBuilderPage"));
const CompanySettingsPage = React.lazy(() => import("@/pages/admin/CompanySettingsPage"));
const CompanyMenuStructurePage = React.lazy(() => import("@/pages/admin/CompanyMenuStructurePage"));
const PluginMarketPage = React.lazy(() => import("@/pages/admin/PluginMarketPage"));
const PluginManagementPage = React.lazy(() => import("@/pages/admin/PluginManagementPage"));
const WorkPatternsPage = React.lazy(() => import("@/pages/admin/WorkPatternsPage"));
const EmployeeProfilesPage = React.lazy(() => import("@/pages/admin/EmployeeProfilesPage"));
const AttendanceManagementPage = React.lazy(() => import("@/pages/admin/AttendanceManagementPage"));
const AttendanceStatusPage = React.lazy(() => import("@/pages/admin/AttendanceStatusPage"));
const AttendanceUnifiedPage = React.lazy(() => import("@/pages/admin/AttendanceUnifiedPage"));
const AttendanceExceptionPage = React.lazy(() => import("@/pages/admin/AttendanceExceptionPage"));
const CommonCodesPage = React.lazy(() => import("@/pages/admin/CommonCodesPage"));
const HolidayManagementPage = React.lazy(() => import("@/pages/admin/HolidayManagementPage"));
const PointManagementPage = React.lazy(() => import("@/pages/admin/PointManagementPage"));
const FinanceManagementPage = React.lazy(() => import("@/pages/admin/FinanceManagementPage"));
const FinancialAnalysisPage = React.lazy(() => import("@/pages/admin/FinancialAnalysisPage"));
const CompanyProfilePage = React.lazy(() => import("@/pages/company-profile/CompanyProfilePage"));
const BusinessManagementPage = React.lazy(() => import("@/pages/admin/BusinessManagementPage"));
const BusinessProjectDetailPage = React.lazy(() => import("@/pages/admin/BusinessProjectDetailPage"));
const LeaveManagementPage = React.lazy(() => import("@/pages/admin/LeaveManagementPage"));
const LeavePolicyPage = React.lazy(() => import("@/pages/admin/LeavePolicyPage"));
const DiligenceSettingsPage = React.lazy(() => import("@/pages/admin/DiligenceSettingsPage"));
const TaskDelayManagementPage = React.lazy(() => import("@/pages/admin/TaskDelayManagementPage"));
const PerformanceManagementPage = React.lazy(() => import("@/pages/admin/PerformanceManagementPage"));
const PerformanceDistributionDetailPage = React.lazy(() => import("@/pages/admin/PerformanceDistributionDetailPage"));
const EmployeeVoiceManagementPage = React.lazy(() => import("@/pages/admin/EmployeeVoiceManagementPage"));
const SystemInquiryPage = React.lazy(() => import("@/pages/admin/SystemInquiryPage"));

// Legal Consultation (법률 상담)
const LegalConsultationListPage = React.lazy(() => import("@/pages/legal/LegalConsultationListPage"));
const LegalConsultationCreatePage = React.lazy(() => import("@/pages/legal/LegalConsultationCreatePage"));
const LegalConsultationDetailPage = React.lazy(() => import("@/pages/legal/LegalConsultationDetailPage"));
const LegalPartnerDirectoryPage = React.lazy(() => import("@/pages/legal/LegalPartnerDirectoryPage"));
const PartnerInboxPage = React.lazy(() => import("@/pages/partner/PartnerInboxPage"));
const PartnerConsultationDetailPage = React.lazy(() => import("@/pages/partner/PartnerConsultationDetailPage"));
const PartnerEngagementsPage = React.lazy(() => import("@/pages/partner/PartnerEngagementsPage"));
const PartnerFirmProfilePage = React.lazy(() => import("@/pages/partner/PartnerFirmProfilePage"));
const LegalPartnerManagementPage = React.lazy(() => import("@/pages/admin/LegalPartnerManagementPage"));
const LegalConsultationModerationPage = React.lazy(() => import("@/pages/admin/LegalConsultationModerationPage"));
const LegalPartnerApplyPage = React.lazy(() => import("@/pages/legal/LegalPartnerApplyPage"));

// Performance
const MyPerformancePage = React.lazy(() => import("@/pages/MyPerformancePage"));
const MyPerformanceDetailPage = React.lazy(() => import("@/pages/MyPerformanceDetailPage"));
const PMProjectDetailPage = React.lazy(() => import("@/pages/PMProjectDetailPage"));

// KPI
const KpiConfigPage = React.lazy(() => import("@/pages/kpi/KpiConfigPage"));
const KpiTargetPage = React.lazy(() => import("@/pages/kpi/KpiTargetPage"));
const KpiDashboardPage = React.lazy(() => import("@/pages/kpi/KpiDashboardPage"));
const KpiMyPage = React.lazy(() => import("@/pages/kpi/KpiMyPage"));
const KpiTeamPage = React.lazy(() => import("@/pages/kpi/KpiTeamPage"));
const KpiRewardPage = React.lazy(() => import("@/pages/kpi/KpiRewardPage"));
// Flow 엔진 (flow-engine 플러그인)
const MyFlowPage = React.lazy(() => import("@/pages/flow/MyFlowPage"));
const FlowDashboardPage = React.lazy(() => import("@/pages/flow/FlowDashboardPage"));
const FlowSettingsPage = React.lazy(() => import("@/pages/flow/FlowSettingsPage"));

// 직원 업무 현황 (employee-work-status 플러그인, super_admin 전용)
const EmployeeWorkStatusPage = React.lazy(() => import("@/pages/employee-work-status/EmployeeWorkStatusPage"));
const EmployeeWorkStatusDetailPage = React.lazy(() => import("@/pages/employee-work-status/EmployeeWorkStatusDetailPage"));

// Competency
const CompetencyAdminPage = React.lazy(() => import("@/pages/competency/CompetencyAdminPage"));
const MyEvaluationTasksPage = React.lazy(() => import("@/pages/competency/MyEvaluationTasksPage"));
const PersonalReportPage = React.lazy(() => import("@/pages/competency/PersonalReportPage"));

// Points
const MyPointsPage = React.lazy(() => import("@/pages/points/MyPointsPage"));
const PointShopPage = React.lazy(() => import("@/pages/points/PointShopPage"));

// Notifications
const NotificationsPage = React.lazy(() => import("@/pages/notifications/NotificationsPage"));
const NotificationSettingsPage = React.lazy(() => import("@/pages/notifications/NotificationSettingsPage"));

// Reservation
const ReservationPage = React.lazy(() => import("@/pages/reservation/ReservationPage"));

// Business Trip
const BusinessTripPage = React.lazy(() => import("@/pages/business-trip/BusinessTripPage"));
const BusinessTripTransferPage = React.lazy(() => import("@/pages/business-trip/BusinessTripTransferPage"));

// Task Order
const TaskOrderPage = React.lazy(() => import("@/pages/task-order/TaskOrderPage"));
const TaskOrderCreatePage = React.lazy(() => import("@/pages/task-order/TaskOrderCreatePage"));
const CooperationCreatePage = React.lazy(() => import("@/pages/task-order/CooperationCreatePage"));
const TaskOrderDetailPage = React.lazy(() => import("@/pages/task-order/TaskOrderDetailPage"));
const TaskOrderDashboardPage = React.lazy(() => import("@/pages/task-order/TaskOrderDashboardPage"));
const TaskOrderScopeGrantsPage = React.lazy(() => import("@/pages/task-order/TaskOrderScopeGrantsPage"));
// const TaskOrderSettingsPage = React.lazy(() => import("@/pages/admin/TaskOrderSettingsPage"));

// Employee Voice
const EmployeeVoicePage = React.lazy(() => import("@/pages/employee-voice/EmployeeVoicePage"));

// Hall of Fame
const HallOfFamePage = React.lazy(() => import("@/pages/hall-of-fame/HallOfFamePage"));

// Lottery
const LotteryManagePage = React.lazy(() => import("@/pages/admin/LotteryManagePage"));
const MyLotteryPage = React.lazy(() => import("@/pages/lottery/MyLotteryPage"));

// Organization
const OrganizationChartPage = React.lazy(() => import("@/pages/organization/OrganizationChartPage"));

// Profile
const ProfilePage = React.lazy(() => import("@/pages/profile/ProfilePage"));
const DesktopAppPage = React.lazy(() => import("@/pages/desktop-app/DesktopAppPage"));

// HR Management
const HRManagementPage = React.lazy(() => import("@/pages/hr/HRManagementPage"));
const ManagedEmployeesPage = React.lazy(() => import("@/pages/hr/ManagedEmployeesPage"));

// Document Request
const DocumentRequestPage = React.lazy(() => import("@/pages/document-request/DocumentRequestPage"));
const MyDocumentRequestPage = React.lazy(() => import("@/pages/document-request/MyDocumentRequestPage"));
const DisciplineAdminPage = React.lazy(() => import("@/pages/discipline/DisciplineAdminPage"));
const DisciplineCreatePage = React.lazy(() => import("@/pages/discipline/DisciplineCreatePage"));
const DisciplineDetailPage = React.lazy(() => import("@/pages/discipline/DisciplineDetailPage"));
const DisciplineStandardsPage = React.lazy(() => import("@/pages/discipline/DisciplineStandardsPage"));
const DisciplineStatsPage = React.lazy(() => import("@/pages/discipline/DisciplineStatsPage"));
const CommitteeReviewPage = React.lazy(() => import("@/pages/discipline/CommitteeReviewPage"));
const DisciplineVotePage = React.lazy(() => import("@/pages/discipline/DisciplineVotePage"));
const MyDisciplinePage = React.lazy(() => import("@/pages/discipline/MyDisciplinePage"));
const MyDisciplineDetailPage = React.lazy(() => import("@/pages/discipline/MyDisciplineDetailPage"));
const OffboardingAdminPage = React.lazy(() => import("@/pages/offboarding/OffboardingAdminPage"));
const OffboardingAdminDetailPage = React.lazy(() => import("@/pages/offboarding/OffboardingAdminDetailPage"));
const MyOffboardingPage = React.lazy(() => import("@/pages/offboarding/MyOffboardingPage"));
// Seal Ledger (직인 사용대장)
const SealLedgerPage = React.lazy(() => import("@/pages/seal/SealLedgerPage"));
const SealRequestPage = React.lazy(() => import("@/pages/seal/SealRequestPage"));
// Policy Center (규정 관리/동의)
const PolicyCenterPage = React.lazy(() => import("@/pages/policy/PolicyCenterPage"));
const MyPoliciesPage = React.lazy(() => import("@/pages/policy/MyPoliciesPage"));
// Training (교육 이수 관리)
const TrainingAdminPage = React.lazy(() => import("@/pages/training/TrainingAdminPage"));
const MyTrainingPage = React.lazy(() => import("@/pages/training/MyTrainingPage"));
// Compliance Oversight (내부신고는 직원의 소리에 임베드 / 정기점검·대시보드)
const ComplianceAdminPage = React.lazy(() => import("@/pages/compliance/ComplianceAdminPage"));
const ComplianceDashboardPage = React.lazy(() => import("@/pages/compliance/ComplianceDashboardPage"));
const ComplianceRegisterPage = React.lazy(() => import("@/pages/compliance/ComplianceRegisterPage"));
const RiskAssessmentPage = React.lazy(() => import("@/pages/compliance/RiskAssessmentPage"));
const ComplianceTemplatesPage = React.lazy(() => import("@/pages/compliance/ComplianceTemplatesPage"));
const ComplianceRoadmapPage = React.lazy(() => import("@/pages/compliance/ComplianceRoadmapPage"));
const LegalDeadlinePage = React.lazy(() => import("@/pages/compliance/LegalDeadlinePage"));
const LegalDeadlineRulesPage = React.lazy(() => import("@/pages/compliance/LegalDeadlineRulesPage"));
const ComplianceObligationCatalogPage = React.lazy(() => import("@/pages/compliance/ComplianceObligationCatalogPage"));
const ComplianceOverviewPage = React.lazy(() => import("@/pages/compliance/ComplianceOverviewPage"));
const CatalogManagePage = React.lazy(() => import("@/pages/super-admin/CatalogManagePage"));
// IPEX (iONE Project EXcellence — SI 수행 방법론 플러그인)
const IpexDashboardPage = React.lazy(() => import("@/pages/ipex/IpexDashboardPage"));
const IpexProjectListPage = React.lazy(() => import("@/pages/ipex/IpexProjectListPage"));
const IpexProjectBoardPage = React.lazy(() => import("@/pages/ipex/IpexProjectBoardPage"));
const IpexProjectSettingsPage = React.lazy(() => import("@/pages/ipex/IpexProjectSettingsPage"));
const IpexRiskRegisterPage = React.lazy(() => import("@/pages/ipex/IpexRiskRegisterPage"));
const IpexMethodologyPage = React.lazy(() => import("@/pages/ipex/IpexMethodologyPage"));
const IpexAdminPage = React.lazy(() => import("@/pages/ipex/IpexAdminPage"));
// 계정 금고 (password-vault 플러그인)
const VaultItemsPage = React.lazy(() => import("@/pages/vault/VaultItemsPage"));
// 행운 로또 (lotto-lab 플러그인)
const LottoPage = React.lazy(() => import("@/pages/lotto/LottoPage"));
const VaultAuditPage = React.lazy(() => import("@/pages/vault/VaultAuditPage"));
// 제안서 작성 관리 (proposal-management 플러그인)
const ProposalListPage = React.lazy(() => import("@/pages/proposal/ProposalListPage"));
const ProposalDetailPage = React.lazy(() => import("@/pages/proposal/ProposalDetailPage"));
const ProposalArchivePage = React.lazy(() => import("@/pages/proposal/ProposalArchivePage"));
const ProposalReviewSettingsPage = React.lazy(() => import("@/pages/proposal/ProposalReviewSettingsPage"));
const ProposalConfigPage = React.lazy(() => import("@/pages/proposal/ProposalConfigPage"));
const ProposalGuidePage = React.lazy(() => import("@/pages/proposal/ProposalGuidePage"));

// Required Document Admin
const RequiredDocumentPage = React.lazy(() => import("@/pages/admin/RequiredDocumentPage"));

// Menu Manuals
const MenuManualsPage = React.lazy(() => import("@/pages/admin/MenuManualsPage"));

// Payslip
const PayslipAdminPage = React.lazy(() => import("@/pages/payslip/PayslipAdminPage"));
const PayslipBatchDetailPage = React.lazy(() => import("@/pages/payslip/PayslipBatchDetailPage"));
const MyPayslipsPage = React.lazy(() => import("@/pages/payslip/MyPayslipsPage"));

// Certificate
const CertificateAdminPage = React.lazy(() => import("@/pages/certificate/CertificateAdminPage"));

// Contract & E-Signature
const ContractSignPage = React.lazy(() => import("@/pages/contract/ContractSignPage"));
const KioskPage = React.lazy(() => import("@/pages/kiosk/KioskPage"));
const QaGuestPage = React.lazy(() => import("@/pages/proposal/QaGuestPage"));
const ContractPreviewPage = React.lazy(() => import("@/pages/contract/ContractPreviewPage"));
const VoidApprovalPage = React.lazy(() => import("@/pages/contract/VoidApprovalPage"));

// Issue
const IssuePage = React.lazy(() => import("@/pages/issue/IssuePage"));
const IssueDetailPage = React.lazy(() => import("@/pages/issue/IssueDetailPage"));

// Maintenance Project
const MaintenanceProjectPage = React.lazy(() => import("@/pages/maintenance-project/MaintenanceProjectPage"));
const MaintenanceProjectDetailPage = React.lazy(() => import("@/pages/maintenance-project/MaintenanceProjectDetailPage"));

// Customer
const CustomerDashboardPage = React.lazy(() => import("@/pages/customer/CustomerDashboardPage"));
const CustomerFeedbackPage = React.lazy(() => import("@/pages/customer-feedback/CustomerFeedbackPage"));
const CustomerProjectListPage = React.lazy(() => import("@/pages/customer/CustomerProjectListPage"));
const CustomerProjectDetailPage = React.lazy(() => import("@/pages/customer/CustomerProjectDetailPage"));

// Business (Employee View)
const BusinessPage = React.lazy(() => import("@/pages/BusinessPage"));
const BusinessDetailPage = React.lazy(() => import("@/pages/BusinessDetailPage"));

// Onboarding
const OnboardingPage = React.lazy(() => import("@/pages/auth/OnboardingPage")); // 개인 온보딩
const OnboardingWizard = React.lazy(() => import("@/pages/onboarding/OnboardingWizardEnhanced")); // 회사 온보딩
const OnboardingOrganization = React.lazy(() => import("@/pages/onboarding/OnboardingOrganization"));

// ─── Loading Fallback ───────────────────────────────────────────────────────

function PageLoader() {
  return (
    <div className="flex h-full min-h-[200px] items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

// Onboarding Router - 개인 온보딩 vs 회사 온보딩 구분
function OnboardingRouter() {
  const { onboardingRequired, companyOnboardingRequired } = useAuthStore();

  // 개인 온보딩이 우선 (직원 개인정보 입력)
  if (onboardingRequired) {
    return (
      <Suspense fallback={<PageLoader />}>
        <OnboardingPage />
      </Suspense>
    );
  }

  // 회사 온보딩 (관리자 회사 설정)
  if (companyOnboardingRequired) {
    return (
      <Suspense fallback={<PageLoader />}>
        <OnboardingWizard />
      </Suspense>
    );
  }

  // 둘 다 아니면 대시보드로 리다이렉트
  return <Navigate to="/dashboard" replace />;
}

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, passwordChangeRequired, consentRequired, onboardingRequired, companyOnboardingRequired, documentSubmissionRequired, docSkipDurationMinutes, noCompany, noPermissionGroup, dailyReportRequired, dailyReportSkipMinutes } = useAuthStore();
  const hasToken = !!getAccessToken();
  const location = useLocation();
  const isWebView = !!(window as any).isReactNativeWebView;
  const logoutMessageSentRef = useRef(false);

  // 디버깅: 온보딩 관련 상태 로그
  useEffect(() => {
    console.log('[ProtectedRoute] Current state:', {
      pathname: location.pathname,
      companyOnboardingRequired,
      onboardingRequired,
      consentRequired,
      passwordChangeRequired,
      isAuthenticated,
      hasToken
    });
  }, [location.pathname, companyOnboardingRequired, onboardingRequired, consentRequired, passwordChangeRequired, isAuthenticated, hasToken]);

  // 건너뛰기: 서버 설정 시간(분)만큼 유효
  const [skippedUntil, setSkippedUntil] = useState<number>(0);
  const isDocSkipped = Date.now() < skippedUntil;
  // 일일 업무 보고 익일 차단 건너뛰기 (관리자 설정 분만큼)
  const [reportSkippedUntil, setReportSkippedUntil] = useState<number>(0);
  const isReportSkipped = Date.now() < reportSkippedUntil;

  // WebView에서 토큰이 없을 때 네이티브 앱에 LOGOUT 메시지 전송
  useEffect(() => {
    if (isWebView && !hasToken && !isLoading && !logoutMessageSentRef.current) {
      logoutMessageSentRef.current = true;
      console.log('[App] No token in WebView, sending LOGOUT message to native app');
      if ((window as any).ReactNativeWebView) {
        (window as any).ReactNativeWebView.postMessage(
          JSON.stringify({ type: 'LOGOUT' })
        );
      }
    }
  }, [isWebView, hasToken, isLoading]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // WebView에서는 token만 있으면 인증된 것으로 처리
  if (isWebView) {
    if (!hasToken) {
      // Don't redirect to login in WebView - show loading screen
      // Native app will handle navigation to login screen
      return (
        <div className="flex h-screen items-center justify-center flex-col gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">로그아웃 처리 중...</p>
        </div>
      );
    }

    // 우선순위별 강제 리다이렉트 (각 조건은 배타적 — 해당 페이지에 있으면 하위 조건 무시)
    if (passwordChangeRequired) {
      if (location.pathname !== "/force-change-password") return <Navigate to="/force-change-password" replace />;
      return <>{children}</>;
    }

    if (consentRequired) {
      if (location.pathname !== "/consent") return <Navigate to="/consent" replace />;
      return <>{children}</>;
    }

    if (onboardingRequired) {
      if (location.pathname !== "/onboarding") return <Navigate to="/onboarding" replace />;
      return <>{children}</>;
    }

    if (companyOnboardingRequired) {
      if (location.pathname !== "/onboarding") return <Navigate to="/onboarding" replace />;
      return <>{children}</>;
    }

    if (documentSubmissionRequired && !isDocSkipped) {
      if (location.pathname !== "/document-submission") {
        return <DocumentSubmissionPage onSkip={() => setSkippedUntil(Date.now() + docSkipDurationMinutes * 60 * 1000)} />;
      }
      return <>{children}</>;
    }

    if (dailyReportRequired && !isReportSkipped) {
      return (
        <DailyReportRequiredPage
          onSkip={dailyReportSkipMinutes > 0 ? () => setReportSkippedUntil(Date.now() + dailyReportSkipMinutes * 60 * 1000) : undefined}
        />
      );
    }

    return <>{children}</>;
  }

  // 일반 웹에서는 기존 로직 유지
  if (!isAuthenticated || !hasToken) {
    return <Navigate to="/login" replace />;
  }

  // 우선순위별 강제 리다이렉트 (각 조건은 배타적 — 해당 페이지에 있으면 하위 조건 무시)
  if (noCompany) {
    if (location.pathname !== "/no-company") return <Navigate to="/no-company" replace />;
    return <>{children}</>;
  }

  if (noPermissionGroup) {
    if (location.pathname !== "/no-permission-group") return <Navigate to="/no-permission-group" replace />;
    return <>{children}</>;
  }

  if (passwordChangeRequired) {
    if (location.pathname !== "/force-change-password") return <Navigate to="/force-change-password" replace />;
    return <>{children}</>;
  }

  if (consentRequired) {
    if (location.pathname !== "/consent") return <Navigate to="/consent" replace />;
    return <>{children}</>;
  }

  if (onboardingRequired) {
    if (location.pathname !== "/onboarding") return <Navigate to="/onboarding" replace />;
    return <>{children}</>;
  }

  if (companyOnboardingRequired) {
    if (location.pathname !== "/onboarding") return <Navigate to="/onboarding" replace />;
    return <>{children}</>;
  }

  if (documentSubmissionRequired && !isDocSkipped) {
    if (location.pathname !== "/document-submission") {
      return <DocumentSubmissionPage onSkip={() => setSkippedUntil(Date.now() + docSkipDurationMinutes * 60 * 1000)} />;
    }
    return <>{children}</>;
  }

  if (dailyReportRequired && !isReportSkipped) {
    return (
      <DailyReportRequiredPage
        onSkip={dailyReportSkipMinutes > 0 ? () => setReportSkippedUntil(Date.now() + dailyReportSkipMinutes * 60 * 1000) : undefined}
      />
    );
  }

  return <>{children}</>;
}

// Menu Protected Route Component - checks menu access permission
function MenuProtectedRoute({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { hasMenuAccess, isLoading, accessiblePaths, authorizedPaths, authorizedPathsLoaded, user } =
    useAuthStore();

  // 로딩 중이거나 메뉴 정보가 아직 로드되지 않은 경우 대기
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // 권한 데이터를 한 번도 못 받은 상태(초기화 중·조회 실패)에서만 통과시킨다.
  // super_admin도 여기서 무조건 통과시키지 않는다 — 인가 경로(authorizedPaths)에
  // 삭제되지 않은 전체 메뉴가 들어 있으므로 정상 메뉴는 그대로 열리고,
  // 삭제된 메뉴·미등록 경로만 차단된다.
  if (user && !authorizedPathsLoaded && accessiblePaths.length === 0 && authorizedPaths.length === 0) {
    return <>{children}</>;
  }

  // 메뉴 접근 권한 확인
  if (!hasMenuAccess(location.pathname)) {
    return <ForbiddenPage />;
  }

  return <>{children}</>;
}

// Employee Only Route Component - restricts access to employees only
function EmployeeOnlyRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore();

  // 고객은 접근 불가
  if (user && isCustomer(user.user_type)) {
    return <ForbiddenPage />;
  }

  return <>{children}</>;
}

// Default Redirect Component - redirects to user's first accessible menu
function DefaultRedirect() {
  const { getDefaultLandingPath } = useAuthStore();
  const defaultPath = getDefaultLandingPath();
  return <Navigate to={defaultPath} replace />;
}

// Public Route Component (redirect to default landing page if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, getDefaultLandingPath, user } = useAuthStore();
  const hasToken = !!getAccessToken();
  const location = useLocation();
  const [initialLoadComplete, setInitialLoadComplete] = React.useState(false);

  // 초기 로딩이 완료되면 플래그 설정
  React.useEffect(() => {
    if (!isLoading && !initialLoadComplete) {
      setInitialLoadComplete(true);
    }
  }, [isLoading, initialLoadComplete]);

  // 초기 로딩 중에만 스피너 표시 (이후 로그인 시도 중에는 로그인 페이지 유지)
  if (isLoading && !initialLoadComplete) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // 로그인 상태에서 초대링크(/register?company=코드)로 들어온 경우는 리다이렉트하지 않는다.
  // RegisterPage가 LoggedInJoinPanel('현재 계정으로 이 회사에 가입 신청')을 표시하도록 통과시킨다.
  const isInviteJoin =
    location.pathname === "/register" &&
    !!new URLSearchParams(location.search).get("company")?.trim();

  // Only redirect if ALL conditions are met: authenticated, has token, AND has user data
  // This prevents redirects during login failures when persist storage is being cleared
  if (isAuthenticated && hasToken && user && !isInviteJoin) {
    const defaultPath = getDefaultLandingPath();
    return <Navigate to={defaultPath} replace />;
  }

  return <>{children}</>;
}

// Radix UI Dialog/Sheet가 열린 상태에서 컴포넌트가 언마운트되면
// body에 pointer-events: none, overflow: hidden 등이 남아 전체 페이지 상호작용 차단됨.
// 라우트 변경 시 이런 잔류 스타일을 정리하는 컴포넌트.
function RadixBodyLockCleanup() {
  const location = useLocation();

  const cleanupBodyLock = useCallback(() => {
    const body = document.body;
    const html = document.documentElement;

    // Radix Dialog가 열린 상태인지 확인 (Portal로 렌더된 열린 다이얼로그가 있으면 정리하지 않음)
    const openDialogs = document.querySelectorAll('[data-state="open"][role="dialog"]');
    if (openDialogs.length > 0) return;

    // pointer-events 잔류 정리
    if (body.style.pointerEvents === 'none') {
      body.style.removeProperty('pointer-events');
    }

    // overflow 잔류 정리
    if (body.style.overflow === 'hidden' && !document.querySelector('[data-radix-scroll-area-viewport]')) {
      body.style.removeProperty('overflow');
    }

    // react-remove-scroll 관련 속성 정리
    if (html.hasAttribute('data-scroll-locked')) {
      html.removeAttribute('data-scroll-locked');
    }

    // padding-right (스크롤바 보정) 정리
    if (body.style.paddingRight) {
      body.style.removeProperty('padding-right');
    }

    // margin-right 정리
    if (body.style.marginRight) {
      body.style.removeProperty('margin-right');
    }

    // --removed-body-scroll-bar-size CSS 변수 정리
    body.style.removeProperty('--removed-body-scroll-bar-size');
  }, []);

  // 라우트 변경 시 정리
  useEffect(() => {
    // 약간의 딜레이를 두어 Radix의 정상적인 cleanup이 먼저 실행되도록 함
    const timer = setTimeout(cleanupBodyLock, 100);
    return () => clearTimeout(timer);
  }, [location.pathname, cleanupBodyLock]);

  // 탭 전환 후 돌아왔을 때 정리 (탭 전환 중 dialog가 닫힌 경우)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setTimeout(cleanupBodyLock, 200);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [cleanupBodyLock]);

  return null;
}

function App() {
  return (
    <BrowserRouter>
      <RadixBodyLockCleanup />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public Routes */}
          <Route
            element={
              <PublicRoute>
                <AuthLayout />
              </PublicRoute>
            }
          >
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/company-register" element={<CompanyRegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
          </Route>

          {/* Contract E-Signature (Public - no auth required) */}
          <Route path="/contract/sign/:token" element={<ContractSignPage />} />
          <Route path="/e-sign/void-approval/:signatureId" element={<VoidApprovalPage />} />

          {/* 외부 징계위원 보안 투표 (Public - 토큰 인증) */}
          <Route path="/discipline/vote/:token" element={<DisciplineVotePage />} />

          {/* 법률 파트너 자가신청 (Public - no auth required) */}
          <Route path="/legal-partner-apply" element={<LegalPartnerApplyPage />} />

          {/* 출퇴근 키오스크 (Public - 디바이스 토큰 인증) */}
          <Route path="/kiosk" element={<KioskPage />} />
          {/* 외부 전문가 의견 요청 — 토큰 링크, 로그인 불필요 */}
          <Route path="/qa-guest/:token" element={<QaGuestPage />} />

          {/* 공개 마케팅 랜딩페이지 (Public - 로그인 여부 무관) */}
          <Route path="/landing" element={<LandingPage />} />
          {/* 메신저 앱 → 브라우저 자동 로그인 인계 (로그인 여부에 따라 교환 또는 바로 이동) */}
          <Route path="/auth/handoff" element={<DesktopHandoffPage />} />

          {/* Force Change Password (Protected but outside MainLayout) */}
          <Route
            path="/force-change-password"
            element={
              <ProtectedRoute>
                <AuthLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<ForceChangePasswordPage />} />
          </Route>

          {/* Consent (Protected but outside MainLayout) */}
          <Route
            path="/consent"
            element={
              <ProtectedRoute>
                <ConsentPage />
              </ProtectedRoute>
            }
          />

          {/* Onboarding (Protected but outside MainLayout) */}
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <OnboardingRouter />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding/organization"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <OnboardingOrganization />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Document Submission (Protected but outside MainLayout) */}
          <Route
            path="/document-submission"
            element={
              <ProtectedRoute>
                <DocumentSubmissionPage />
              </ProtectedRoute>
            }
          />

          {/* No Company (Protected but outside MainLayout) */}
          <Route
            path="/no-company"
            element={
              <ProtectedRoute>
                <NoCompanyPage />
              </ProtectedRoute>
            }
          />

          {/* Billing locked screen (Protected but outside MainLayout) */}
          <Route
            path="/billing/locked"
            element={
              <ProtectedRoute>
                <BillingLockedPage />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes */}
          <Route
            element={
              <ProtectedRoute>
                <MenuProtectedRoute>
                  <SubscriptionGate>
                    <MainLayout />
                  </SubscriptionGate>
                </MenuProtectedRoute>
              </ProtectedRoute>
            }
          >
            {/* Default Landing - redirects to first accessible menu */}
            <Route path="/" element={<DefaultRedirect />} />

            {/* Dashboard */}
            <Route path="/dashboard" element={<DashboardPage />} />

            {/* Maintenance */}
            <Route path="/maintenance" element={<MaintenanceDashboardPage />} />
            <Route path="/maintenance/tickets" element={<TicketListPage />} />
            <Route path="/maintenance/tickets/new" element={<TicketCreatePage />} />
            <Route path="/maintenance/tickets/:id" element={<TicketDetailPage />} />

            {/* SLA Dashboard */}
            <Route path="/sla" element={<SLADashboardPage />} />
            <Route path="/sla/dashboard" element={<SLADashboardPage />} />

            {/* Projects */}
            <Route path="/projects" element={<ProjectListPage />} />
            <Route path="/projects/board" element={<ProjectKanbanPage />} />
            <Route path="/projects/:id" element={<ProjectDetailPage />} />
            <Route path="/projects/:id/board" element={<ProjectBoardPage />} />
            <Route path="/projects/:id/gantt" element={<ProjectGanttPage />} />
            <Route path="/projects/:id/sla" element={<ProjectSLADashboardPage />} />
            <Route path="/projects/:id/posts" element={<ProjectBoardPostsPage />} />
            <Route path="/projects/:id/settings" element={<ProjectSettingsPage />} />
            <Route path="/my-tasks" element={<MyTasksPage />} />
            <Route path="/tasks/employee-gantt" element={<EmployeeWorkloadGanttPage />} />

            {/* Chat - Employee Only */}
            <Route path="/chat" element={<EmployeeOnlyRoute><ChatPage /></EmployeeOnlyRoute>} />

            {/* Calendar */}
            <Route path="/calendar" element={<CalendarPage />} />

            {/* Files */}
            <Route path="/files" element={<FilesPage />} />
            <Route path="/files/:folderId" element={<FilesPage />} />
            {/* OS 공유하기(Web Share Target) 수신 화면 — manifest.share_target action */}
            <Route path="/share-receive" element={<ShareReceivePage />} />

            {/* Sticky Notes */}
            <Route path="/sticky-notes" element={<StickyNotesPage />} />

            {/* Legal Consultation (법률 상담 - 클라이언트) */}
            <Route path="/legal" element={<LegalConsultationListPage />} />
            <Route path="/legal/consultations" element={<LegalConsultationListPage />} />
            <Route path="/legal/consultations/new" element={<LegalConsultationCreatePage />} />
            <Route path="/legal/consultations/:id" element={<LegalConsultationDetailPage />} />
            <Route path="/legal/kb" element={<LegalConsultationListPage />} />
            <Route path="/legal/partners" element={<LegalPartnerDirectoryPage />} />

            {/* Legal Partner Portal (파트너 포털) */}
            <Route path="/partner" element={<PartnerInboxPage />} />
            <Route path="/partner/inbox" element={<PartnerInboxPage />} />
            <Route path="/partner/consultations/:id" element={<PartnerConsultationDetailPage />} />
            <Route path="/partner/engagements" element={<PartnerEngagementsPage />} />
            <Route path="/partner/firm" element={<PartnerFirmProfilePage />} />

            {/* Legal Admin (super_admin) */}
            <Route path="/admin/legal-partners" element={<LegalPartnerManagementPage />} />
            <Route path="/admin/legal-consultations" element={<LegalConsultationModerationPage />} />

            {/* Board */}
            <Route path="/board" element={<BoardListPage />} />
            <Route path="/board/notice" element={<NoticeBoardPage />} />
            <Route path="/board/free" element={<FreeBoardPage />} />
            <Route path="/board/archive" element={<ArchiveBoardPage />} />
            <Route path="/board/weekly-report" element={<WeeklyReportBoardPage />} />
            <Route path="/board/:type" element={<BoardListPage />} />
            <Route path="/board/:type/:id" element={<BoardDetailPage />} />

            {/* 전자결재 v2 (EA) */}
            <Route path="/ea" element={<Navigate to="/ea/inbox" replace />} />
            <Route path="/ea/inbox" element={<EaInboxPage />} />
            <Route path="/ea/new" element={<EaComposePage />} />
            <Route path="/ea/new/:typeCode" element={<EaComposePage />} />
            <Route path="/ea/documents/:id" element={<EaDocumentPage />} />
            <Route path="/ea/documents/:id/edit" element={<EaComposePage />} />
            <Route path="/ea/processing" element={<EaProcessingPage />} />
            <Route path="/ea/settings" element={<EaSettingsPage />} />

            {/* 구 전자결재 — 모든 /approval/* 경로는 EA 로 리다이렉트 (구 코어 삭제) */}
            <Route path="/approval/pending" element={<EaLegacyRedirect kind="approval_list" />} />
            <Route path="/approval/my" element={<EaLegacyRedirect kind="approval_list" />} />
            <Route path="/approval/create" element={<EaLegacyRedirect kind="approval_list" />} />
            <Route path="/approval/drafts" element={<EaLegacyRedirect kind="approval_list" />} />
            <Route path="/approval/completed" element={<EaLegacyRedirect kind="approval_list" />} />
            <Route path="/approval/:id" element={<EaLegacyRedirect kind="approval_doc" />} />

            {/* Attendance */}
            <Route path="/attendance" element={<AttendancePage />} />
            <Route path="/attendance/leave" element={<LeavePage />} />
            <Route path="/attendance/weekly-schedule" element={<MyWeeklySchedulePage />} />
            <Route path="/attendance/team-schedule" element={<TeamWeeklySchedulePage />} />
            <Route path="/my-half-day-leaves" element={<HalfDayLeavePage />} />

            {/* Points */}
            <Route path="/points" element={<MyPointsPage />} />
            <Route path="/points/shop" element={<PointShopPage />} />

            {/* Worklog */}
            <Route path="/worklog" element={<WorklogPage />} />
            <Route path="/worklog/settings" element={<DailyReportSettingsPage />} />
            {/* 구 주간보고 → 주간보고서(자동)로 통합 (wlmerge01) */}
            <Route path="/weekly-report" element={<Navigate to="/weekly-report-pro" replace />} />
            <Route path="/weekly-report-pro" element={<WeeklyReportProPage />} />

            {/* 구 지출 화면 → EA 리다이렉트 (구 컴포넌트는 ea09 정리 때 삭제) */}
            <Route path="/expense" element={<EaLegacyRedirect kind="expense" />} />
            <Route path="/purchase-request" element={<EaLegacyRedirect kind="purchase" />} />
            <Route path="/card-approval" element={<EaLegacyRedirect kind="card" />} />

            {/* My Card Usage (Employee) */}
            <Route path="/my-card" element={<MyCardUsagePage />} />

            {/* Corporate Card Admin */}
            <Route path="/admin/corporate-cards" element={<CorporateCardManagementPage />} />
            <Route path="/admin/corporate-cards/upload" element={<CardUsageUploadPage />} />
            <Route path="/admin/corporate-cards/:id/usages" element={<CardUsageDetailPage />} />

            {/* 구 지출 결재(approval-management) → EA 처리함/문서 */}
            <Route path="/approval-management" element={<EaLegacyRedirect kind="management" />} />

            {/* Notifications */}
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/settings/notifications" element={<NotificationSettingsPage />} />

            {/* Reservation */}
            <Route path="/reservation" element={<ReservationPage />} />

            {/* Business Trip */}
            <Route path="/business-trips" element={<BusinessTripPage />} />
            <Route path="/business-trips/transfer" element={<BusinessTripTransferPage />} />

            {/* Task Order */}
            <Route path="/task-orders" element={<TaskOrderPage />} />
            <Route path="/task-orders/create" element={<TaskOrderCreatePage />} />
            <Route path="/task-orders/cooperation" element={<CooperationCreatePage />} />
            <Route path="/task-orders/dashboard" element={<TaskOrderDashboardPage />} />
            {/* 범위 위임 관리(관리자) — /task-orders 메뉴 하위 경로라 별도 메뉴 없이 접근, 데이터는 백엔드 admin 게이트 */}
            <Route path="/task-orders/scope-grants" element={<TaskOrderScopeGrantsPage />} />
            <Route path="/task-orders/:id" element={<TaskOrderDetailPage />} />
            {/* <Route path="/admin/task-order-settings" element={<TaskOrderSettingsPage />} /> */}

            {/* Discipline (직원 징계 관리) */}
            <Route path="/discipline/admin" element={<DisciplineAdminPage />} />
            <Route path="/discipline/admin/create" element={<DisciplineCreatePage />} />
            <Route path="/discipline/admin/:id" element={<DisciplineDetailPage />} />
            {/* Discipline 등록자(본부장) 전용 — 같은 컴포넌트 재사용, capability로 렌더 제한 */}
            <Route path="/discipline/register" element={<DisciplineAdminPage />} />
            <Route path="/discipline/register/create" element={<DisciplineCreatePage />} />
            <Route path="/discipline/register/:id" element={<DisciplineDetailPage />} />
            <Route path="/discipline/standards" element={<DisciplineStandardsPage />} />
            <Route path="/discipline/stats" element={<DisciplineStatsPage />} />
            <Route path="/discipline/committee" element={<CommitteeReviewPage />} />
            <Route path="/discipline/my" element={<MyDisciplinePage />} />
            <Route path="/discipline/my/:id" element={<MyDisciplineDetailPage />} />

            {/* 퇴사 프로세스 (Offboarding) */}
            <Route path="/offboarding/admin" element={<OffboardingAdminPage />} />
            <Route path="/offboarding/admin/:id" element={<OffboardingAdminDetailPage />} />
            <Route path="/offboarding/my" element={<MyOffboardingPage />} />

            {/* 직인 사용대장 (Seal Ledger) */}
            <Route path="/seal/ledger" element={<SealLedgerPage />} />
            <Route path="/seal/request" element={<SealRequestPage />} />

            {/* 규정 관리/동의 (Policy Center) */}
            <Route path="/policy/center" element={<PolicyCenterPage />} />
            <Route path="/policy/my" element={<MyPoliciesPage />} />

            {/* 교육 이수 관리 (Training) */}
            <Route path="/training/admin" element={<TrainingAdminPage />} />
            <Route path="/training/my" element={<MyTrainingPage />} />

            {/* 정기점검·대시보드 (Compliance Oversight) — 내부신고는 /employee-voice 에 임베드 */}
            <Route path="/compliance/admin" element={<ComplianceAdminPage />} />
            <Route path="/compliance/dashboard" element={<ComplianceDashboardPage />} />
            {/* 준법 기록대장 + 위험성평가 */}
            <Route path="/compliance/register" element={<ComplianceRegisterPage />} />
            <Route path="/compliance/risk" element={<RiskAssessmentPage />} />
            {/* 표준 템플릿: 기업 도입(/compliance/templates) vs 본사 관리(/super-admin/compliance-catalog) 분리 */}
            <Route path="/compliance/templates" element={<ComplianceTemplatesPage />} />
            <Route path="/super-admin/compliance-catalog" element={<CatalogManagePage />} />
            {/* 성장단계 진단·점수·로드맵 (기업) + 리스크 항목 카탈로그 (본사) */}
            <Route path="/compliance/roadmap" element={<ComplianceRoadmapPage />} />
            {/* 법정기한 관리 (체크리스트 — 실무자) + 법정기한 규칙 (설정 — 관리자) */}
            <Route path="/compliance/deadlines" element={<LegalDeadlinePage />} />
            <Route path="/compliance/deadline-rules" element={<LegalDeadlineRulesPage />} />
            {/* 연차사용촉진: 별도 라우트 없음 — 관리는 /compliance/deadlines?view=promotion,
                직원 회신은 /attendance/leave 에 임베드 (메뉴 미등록 경로는 MenuProtectedRoute 403) */}
            <Route path="/compliance/obligation-catalog" element={<ComplianceObligationCatalogPage />} />
            <Route path="/compliance/overview" element={<ComplianceOverviewPage />} />

            {/* IPEX (iONE Project EXcellence — SI 수행 방법론 플러그인) */}
            <Route path="/ipex/dashboard" element={<IpexDashboardPage />} />
            <Route path="/ipex/projects" element={<IpexProjectListPage />} />
            <Route path="/ipex/projects/:id" element={<IpexProjectBoardPage />} />
            {/* 프로젝트 설정(기본·역할·참여자·고객사·해제) — ipex-projects 메뉴 하위 경로라 기존 가드로 허용됨 */}
            <Route path="/ipex/projects/:id/settings" element={<IpexProjectSettingsPage />} />
            {/* 직원용 고객 화면 미리보기 — ipex-projects 메뉴 하위 경로라 기존 가드로 허용됨 */}
            <Route path="/ipex/projects/:id/customer-preview" element={<CustomerProjectDetailPage />} />
            <Route path="/ipex/risks" element={<IpexRiskRegisterPage />} />
            <Route path="/ipex/methodology" element={<IpexMethodologyPage />} />
            <Route path="/ipex/admin" element={<IpexAdminPage />} />

            {/* 계정 금고 (password-vault 플러그인)
                /vault 는 사이드바 그룹 헤더의 경로 — 실제 화면은 /vault/items */}
            <Route path="/vault" element={<Navigate to="/vault/items" replace />} />
            <Route path="/vault/items" element={<VaultItemsPage />} />
            <Route path="/vault/audit" element={<VaultAuditPage />} />

            {/* 행운 로또 (lotto-lab 플러그인) */}
            <Route path="/lotto" element={<LottoPage />} />

            {/* 제안서 작성 관리 (proposal-management 플러그인)
                /proposals 는 사이드바 그룹 헤더의 경로 — 실제 화면은 /proposals/projects */}
            <Route path="/proposals" element={<Navigate to="/proposals/projects" replace />} />
            <Route path="/proposals/projects" element={<ProposalListPage />} />
            <Route path="/proposals/projects/:id" element={<ProposalDetailPage />} />
            {/* 섹션별 딥링크 — 허브 카드·알림에서 특정 기능 화면으로 바로 진입 */}
            <Route path="/proposals/projects/:id/:section" element={<ProposalDetailPage />} />
            <Route path="/proposals/archive" element={<ProposalArchivePage />} />
            {/* 평가 기준: 별도 메뉴 없음 — /proposals 프리픽스로 인가, 편집은 서버측 admin 게이트 */}
            <Route path="/proposals/review-settings" element={<ProposalReviewSettingsPage />} />
            {/* 제안 설정: 'proposal-settings' 메뉴(admin 전용) — AI 모델·평가 기준·나라장터 통합 */}
            <Route path="/proposals/settings" element={<ProposalConfigPage />} />
            {/* 사용 안내: 별도 메뉴 없음 — /proposals 프리픽스 인가, 전 직원 열람 */}
            <Route path="/proposals/guide" element={<ProposalGuidePage />} />

            {/* Employee Voice */}
            <Route path="/employee-voice" element={<EmployeeVoicePage />} />

            {/* Hall of Fame */}
            <Route path="/hall-of-fame" element={<HallOfFamePage />} />

            {/* Lottery */}
            <Route path="/lottery" element={<MyLotteryPage />} />

            {/* Organization - Employee Only */}
            <Route path="/organization" element={<EmployeeOnlyRoute><OrganizationChartPage /></EmployeeOnlyRoute>} />

            {/* Admin */}
            <Route path="/admin" element={<AdminDashboardV2 />} />
            <Route path="/admin/users" element={<Navigate to="/admin/company-settings?tab=users" replace />} />
            <Route path="/admin/customers" element={<CustomerManagementPage />} />
            <Route path="/admin/departments" element={<Navigate to="/admin/company-settings?tab=departments" replace />} />
            <Route path="/admin/permission-groups" element={<AdminPermissionGroupsPage />} />
            <Route path="/admin/menus" element={<AdminMenusPage />} />
            <Route path="/admin/permission-group-menus" element={<PermissionGroupMenuBuilderPage />} />
            <Route path="/admin/company-settings" element={<CompanySettingsPage />} />
            <Route path="/admin/company-menu-structure" element={<CompanyMenuStructurePage />} />
            <Route path="/admin/plugin-market" element={<PluginMarketPage />} />
            <Route path="/admin/plugin-management" element={<PluginManagementPage />} />
            <Route path="/admin/file-trash" element={<FileTrashPage />} />
            <Route path="/admin/file-logs" element={<FileLogsPage />} />
            <Route path="/admin/settings" element={<SystemSettings />} />
            <Route path="/admin/audit-logs" element={<AuditLogPage />} />
            <Route path="/admin/consent-records" element={<ConsentRecordsPage />} />
            <Route path="/admin/consent-documents" element={<ConsentDocumentManagePage />} />
            <Route path="/admin/notifications" element={<NotificationManagementPage />} />
            <Route path="/admin/scheduled-notifications" element={<ScheduledNotificationsPage />} />
            <Route path="/admin/work-patterns" element={<WorkPatternsPage />} />
            <Route path="/admin/employee-profiles" element={<EmployeeProfilesPage />} />
            <Route path="/admin/attendance" element={<AttendanceManagementPage />} />
            <Route path="/admin/attendance-status" element={<AttendanceStatusPage />} />
            <Route path="/admin/attendance-unified" element={<AttendanceUnifiedPage />} />
            <Route path="/admin/attendance-exceptions" element={<AttendanceExceptionPage />} />
            <Route path="/admin/half-day-leaves" element={<HalfDayLeaveManagementPage />} />
            <Route path="/admin/half-day-leaves/manual-grant" element={<ManualHalfDayGrantPage />} />
            <Route path="/admin/half-day-leaves/rules" element={<HalfDayLeaveRulesPage />} />
            <Route path="/admin/common-codes" element={<CommonCodesPage />} />
            <Route path="/admin/holidays" element={<HolidayManagementPage />} />
            <Route path="/admin/points" element={<PointManagementPage />} />
            <Route path="/admin/finance" element={<FinanceManagementPage />} />
            <Route path="/admin/financial-analysis" element={<FinancialAnalysisPage />} />
            <Route path="/company-profile" element={<CompanyProfilePage />} />
            <Route path="/admin/business" element={<BusinessManagementPage />} />
            <Route path="/admin/business/:id" element={<BusinessProjectDetailPage />} />
            {/* 구 팀 업무일지 → 업무일지 > 전체 업무일지 탭으로 통합 (wlteam01) */}
            <Route path="/admin/team-worklog" element={<Navigate to="/worklog?tab=team" replace />} />
            <Route path="/admin/leave-management" element={<LeaveManagementPage />} />
            <Route path="/admin/leave-policy" element={<LeavePolicyPage />} />
            <Route path="/admin/diligence-settings" element={<DiligenceSettingsPage />} />
            <Route path="/admin/task-delay-management" element={<TaskDelayManagementPage />} />
            <Route path="/admin/performance" element={<PerformanceManagementPage />} />
            <Route path="/admin/performance/distribution/:distributionId" element={<PerformanceDistributionDetailPage />} />
            <Route path="/admin/employee-voices" element={<EmployeeVoiceManagementPage />} />
            <Route path="/admin/system-inquiry" element={<SystemInquiryPage mode="submit" />} />
            <Route path="/super-admin/system-inquiry" element={<SystemInquiryPage mode="manage" />} />
            <Route path="/admin/lottery" element={<LotteryManagePage />} />
            <Route path="/admin/lottery-prizes" element={<Navigate to="/admin/lottery" replace />} />
            <Route path="/admin/lottery-winners" element={<Navigate to="/admin/lottery" replace />} />
            <Route path="/admin/company-approval" element={<CompanyManagementPage />} />
            <Route path="/admin/companies" element={<SuperAdminCompaniesPage />} />
            <Route path="/admin/company-config" element={<SuperAdminCompanyConfigPage />} />
            <Route path="/super-admin/default-menus" element={<DefaultMenuSettingsPage />} />
            <Route path="/super-admin/ai-settings" element={<AISettingsPage />} />
            <Route path="/super-admin/ai-usage" element={<AIUsagePage />} />
            <Route path="/super-admin/members" element={<SuperAdminMembersPage />} />

            {/* Billing / Subscription */}
            <Route path="/billing" element={<BillingPage />} />
            <Route path="/billing/plans" element={<PlansPage />} />
            <Route path="/super-admin/billing/plans" element={<PlansManagePage />} />
            <Route path="/super-admin/billing/inquiries" element={<BillingInquiriesPage />} />
            <Route path="/super-admin/billing/settings" element={<BillingSettingsPage />} />
            <Route path="/super-admin/revenue" element={<RevenueDashboardPage />} />
            <Route path="/admin/resource-usage" element={<ResourceUsagePage />} />
            <Route path="/admin/storage-usage" element={<StorageUsagePage />} />
            <Route path="/admin/required-documents" element={<RequiredDocumentPage />} />
            <Route path="/admin/menu-manuals" element={<MenuManualsPage />} />

            {/* My Performance */}
            <Route path="/my-performance" element={<MyPerformancePage />} />
            <Route path="/my-performance/detail/:distributionId" element={<MyPerformanceDetailPage />} />
            <Route path="/my-performance/pm-projects/:projectId" element={<PMProjectDetailPage />} />

            {/* KPI Management */}
            <Route path="/kpi/config" element={<KpiConfigPage />} />
            <Route path="/kpi/targets" element={<KpiTargetPage />} />
            <Route path="/kpi/dashboard" element={<KpiDashboardPage />} />
            <Route path="/kpi/my" element={<KpiMyPage />} />
            <Route path="/kpi/team" element={<KpiTeamPage />} />
            <Route path="/kpi/rewards" element={<KpiRewardPage />} />

            {/* Flow 엔진 (flow-engine 플러그인) */}
            <Route path="/flow" element={<MyFlowPage />} />
            <Route path="/flow/dashboard" element={<FlowDashboardPage />} />
            <Route path="/flow/settings" element={<FlowSettingsPage />} />

            {/* 직원 업무 현황 (employee-work-status 플러그인, super_admin 전용) */}
            <Route path="/employee-work-status" element={<EmployeeWorkStatusPage />} />
            <Route path="/employee-work-status/:userId" element={<EmployeeWorkStatusDetailPage />} />

            {/* Competency Evaluation */}
            <Route path="/competency/my-tasks" element={<MyEvaluationTasksPage />} />
            <Route path="/competency/admin" element={<CompetencyAdminPage />} />
            <Route path="/competency/report/:periodId/:userId" element={<PersonalReportPage />} />

            {/* Business (Employee View - Read Only) */}
            <Route path="/business" element={<EmployeeOnlyRoute><BusinessPage /></EmployeeOnlyRoute>} />
            <Route path="/business/:projectId" element={<EmployeeOnlyRoute><BusinessDetailPage /></EmployeeOnlyRoute>} />

            {/* Profile */}
            <Route path="/profile" element={<ProfilePage />} />

            {/* Windows 메신저 설치 파일 다운로드 — 메뉴 없는 화면, authStore alwaysAllowedPaths 에 등록 */}
            <Route path="/desktop-app" element={<DesktopAppPage />} />

            {/* HR Management */}
            <Route path="/hr/employees" element={<HRManagementPage />} />
            <Route path="/managed-employees" element={<ManagedEmployeesPage />} />
            <Route path="/hr/user-approval" element={<Navigate to="/hr/employees" replace />} />

            {/* Employee Status */}
            <Route path="/employee-status" element={<EmployeeStatusPage />} />

            {/* Document Requests */}
            <Route path="/document-requests/admin" element={<DocumentRequestPage />} />
            <Route path="/document-requests/my" element={<MyDocumentRequestPage />} />

            {/* Payslip (급여명세서) */}
            <Route path="/payslip/admin" element={<PayslipAdminPage />} />
            <Route path="/payslip/admin/:batchId" element={<PayslipBatchDetailPage />} />
            <Route path="/payslip" element={<MyPayslipsPage />} />

            {/* Certificate (증명서) */}
            <Route path="/certificate/admin" element={<CertificateAdminPage />} />

            {/* Contract Preview (계약서 미리보기 - 로그인 필요) */}
            <Route path="/contract/preview/:documentId" element={<ContractPreviewPage />} />

            {/* Issues (이슈 관리) */}
            <Route path="/issues" element={<IssuePage />} />
            <Route path="/issues/:id" element={<IssueDetailPage />} />
            <Route path="/maintenance-projects" element={<MaintenanceProjectPage />} />
            <Route path="/maintenance-projects/:id" element={<MaintenanceProjectDetailPage />} />
            <Route path="/maintenance-projects/:id/:section" element={<MaintenanceProjectDetailPage />} />

            {/* Customer (고객 전용) */}
            <Route path="/customer" element={<CustomerDashboardPage />} />
            {/* IPEX 고객 포털 — 고객사에 공개된 사업만 (인가는 서버의 고객사 일치 + 공개 스위치 판정) */}
            <Route path="/customer/projects" element={<CustomerProjectListPage />} />
            <Route path="/customer/projects/:id" element={<CustomerProjectDetailPage />} />
            <Route path="/customer-feedback" element={<CustomerFeedbackPage />} />
          </Route>

          {/* 403 Forbidden */}
          <Route path="/forbidden" element={<ForbiddenPage />} />

          {/* No Permission Group */}
          <Route path="/no-permission-group" element={<NoPermissionGroupPage />} />

          {/* EA 문서 인쇄 (standalone without layout) */}
          <Route
            path="/ea/documents/:id/print"
            element={
              <ProtectedRoute>
                <EmployeeOnlyRoute>
                  <MenuProtectedRoute>
                    <EaPrintPage />
                  </MenuProtectedRoute>
                </EmployeeOnlyRoute>
              </ProtectedRoute>
            }
          />

          {/* Chat Popup (standalone without layout) - Employee + 채팅 위젯 권한 */}
          <Route
            path="/chat/popup/:roomId"
            element={
              <ProtectedRoute>
                <EmployeeOnlyRoute>
                  <MenuProtectedRoute>
                    <ChatPopupPage />
                  </MenuProtectedRoute>
                </EmployeeOnlyRoute>
              </ProtectedRoute>
            }
          />

          {/* 404 - must be last (redirects to landing page which handles user-specific routing) */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <Toaster />
    </BrowserRouter>
  );
}

export default App;
